import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useSearch } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, ArrowRight, BookOpen, Check, ChevronDown, ChevronUp, Clock, ClipboardList, Hourglass, Layers, Loader2, RotateCcw, Save, Send, Sparkles, Star, Target, Lightbulb, Trophy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Illustration } from "@/components/Illustration";
import { Confetti } from "@/components/Confetti";
import { TaskText } from "@/components/performance/TaskText";
import { SourceViewer } from "@/components/performance/SourceViewer";
import { ItemInput } from "@/components/performance/ItemInput";
import { isAnswered, ITEM_TYPE_LABEL, type TaskDocument, type TaskItem, type ItemScore } from "@shared/performanceTasks";
import { subjectUi, LEVEL_UI, formatDuration, type TaskPayload, type AttemptPayload } from "@/lib/performanceTasks";

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

export default function PerformanceTaskPlayer() {
  const { slug } = useParams<{ slug: string }>();
  const search = useSearch();
  const attemptId = new URLSearchParams(search).get("attempt");
  const [, setLocation] = useLocation();
  const { isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const { toast } = useToast();

  const taskQ = useQuery<TaskPayload>({ queryKey: ["/api/performance-tasks", slug], queryFn: () => getJson(`/api/performance-tasks/${slug}`), enabled: isAuthenticated && !!slug });
  const attemptQ = useQuery<AttemptPayload>({
    queryKey: ["/api/performance-tasks/attempts", attemptId],
    queryFn: () => getJson(`/api/performance-tasks/attempts/${attemptId}`),
    enabled: isAuthenticated && !!attemptId,
    refetchInterval: q => (q.state.data?.attempt.status === "submitted" ? 12_000 : false),
  });

  const start = useMutation({
    mutationFn: async (restart: boolean) => (await apiRequest("POST", `/api/performance-tasks/${slug}/attempts`, { restart })).json() as Promise<{ id: string }>,
    onSuccess: (a) => { qc.invalidateQueries({ queryKey: ["/api/performance-tasks"] }); setLocation(`/performance-tasks/${slug}?attempt=${a.id}`, { replace: true }); },
    onError: () => toast({ title: "Could not start the task", variant: "destructive" }),
  });

  useEffect(() => { window.scrollTo({ top: 0 }); }, [attemptId]);

  if (taskQ.isLoading || (attemptId && attemptQ.isLoading)) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (taskQ.isError || !taskQ.data) {
    return <div className="min-h-screen flex items-center justify-center p-6"><Card className="rounded-xl max-w-md"><CardContent className="p-6 text-center space-y-3"><Illustration name="owl-search" size={100} /><p className="font-semibold">That task is not available.</p><Button className="rounded-lg" onClick={() => setLocation("/performance-tasks")}>Back to tasks</Button></CardContent></Card></div>;
  }
  const task = taskQ.data;

  if (!attemptId || attemptQ.isError || !attemptQ.data) {
    return <Intro payload={task} starting={start.isPending} onStart={() => start.mutate(false)} onOpen={id => setLocation(`/performance-tasks/${slug}?attempt=${id}`)} onBack={() => setLocation("/performance-tasks")} />;
  }
  if (attemptQ.data.attempt.status === "in_progress") {
    return <Workspace key={attemptQ.data.attempt.id} payload={attemptQ.data} onSubmitted={() => { qc.invalidateQueries({ queryKey: ["/api/performance-tasks"] }); attemptQ.refetch(); }} onExit={() => setLocation("/performance-tasks")} />;
  }
  return <Results payload={attemptQ.data} onAgain={() => start.mutate(false)} starting={start.isPending} onBack={() => setLocation("/performance-tasks")} />;
}

// ---------- Intro ----------

function Intro({ payload, onStart, starting, onOpen, onBack }: { payload: TaskPayload; onStart: () => void; starting: boolean; onOpen: (id: string) => void; onBack: () => void }) {
  const { summary, document: doc, attempts } = payload;
  const ui = subjectUi(summary.subject);
  const inProgress = attempts.find(a => a.status === "in_progress");
  return (
    <div className="min-h-screen bg-background">
      <div className="bg-primary text-primary-foreground p-4 md:p-6">
        <div className="container max-w-4xl mx-auto">
          <Button variant="ghost" className="text-white hover:bg-white/20 p-2 mb-2" onClick={onBack} data-testid="button-back-tasks"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold">{summary.subject}</span>
            <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold">Grade {summary.grade}</span>
            <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold">Performance Task</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{summary.title}</h1>
          <p className="text-white/90 mt-1">{summary.description}</p>
        </div>
      </div>
      <div className="container max-w-4xl mx-auto p-4 md:p-6 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Clock, label: "Time", value: `${summary.timeMinutes} min` },
            { icon: Layers, label: "Parts", value: `${summary.partCount}` },
            { icon: ClipboardList, label: "Questions", value: `${summary.itemCount}` },
            { icon: Sparkles, label: "XP", value: `up to ${summary.xpReward}` },
          ].map(s => (
            <div key={s.label} className={`rounded-xl border ${ui.card} p-3 flex items-center gap-3`}>
              <span className={`w-9 h-9 rounded-lg ${ui.sticker} flex items-center justify-center shrink-0`}><s.icon className="w-5 h-5" /></span>
              <div><p className="text-xs text-muted-foreground">{s.label}</p><p className="font-bold tabular-nums">{s.value}</p></div>
            </div>
          ))}
        </div>

        <Card className="rounded-xl"><CardContent className="p-4 md:p-5 space-y-3">
          <h2 className="font-bold text-lg">Introduction to task</h2>
          <TaskText text={doc.intro} className="text-sm" />
          {doc.youWillNeedTo && doc.youWillNeedTo.length > 0 && (
            <div className="rounded-xl border border-border bg-muted/30 p-3">
              <p className="font-semibold text-sm mb-1">You will need to:</p>
              <ul className="list-disc pl-5 text-sm space-y-0.5">{doc.youWillNeedTo.map((b, i) => <li key={i}>{b}</li>)}</ul>
            </div>
          )}
          {doc.sources.length > 0 && <p className="text-sm text-muted-foreground inline-flex items-center gap-1"><BookOpen className="w-4 h-4" /> {doc.sources.length} source{doc.sources.length === 1 ? "" : "s"} to read. You can look back at them as often as you like.</p>}
        </CardContent></Card>

        <Card className="rounded-xl border-primary/30"><CardContent className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <Illustration name="owl-rocket" size={84} className="shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Ready when you are</p>
            <p className="text-sm text-muted-foreground">Your answers save automatically. In the real exam you get {summary.timeMinutes} minutes, but here you can take the time you need.</p>
          </div>
          {inProgress ? (
            <Button className="rounded-xl font-bold" onClick={() => onOpen(inProgress.id)} data-testid="button-continue-task">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
          ) : (
            <Button className="rounded-xl font-bold" onClick={onStart} disabled={starting} data-testid="button-start-task">{starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />} Start task</Button>
          )}
        </CardContent></Card>

        {attempts.filter(a => a.status !== "in_progress").length > 0 && (
          <Card className="rounded-xl"><CardContent className="p-4 md:p-5">
            <h2 className="font-semibold mb-2">Your attempts</h2>
            <ul className="divide-y divide-border">
              {attempts.filter(a => a.status !== "in_progress").map(a => (
                <li key={a.id} className="py-2 flex items-center gap-3 text-sm">
                  <span className="font-medium w-24">Attempt {a.attemptNumber}</span>
                  {a.status === "graded" ? <><span className="tabular-nums">{a.score} / {a.maxScore}</span>{a.level && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${LEVEL_UI[a.level].chip}`}>{a.level}</span>}</> : <span className="text-muted-foreground inline-flex items-center gap-1"><Hourglass className="w-3.5 h-3.5" /> {a.status === "submitted" ? "Being marked" : "Marking delayed"}</span>}
                  <Button variant="outline" size="sm" className="ml-auto rounded-lg" onClick={() => onOpen(a.id)} data-testid={`button-attempt-${a.attemptNumber}`}>Open</Button>
                </li>
              ))}
            </ul>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}

// ---------- Workspace ----------

function Workspace({ payload, onSubmitted, onExit }: { payload: AttemptPayload; onSubmitted: () => void; onExit: () => void }) {
  const { attempt, task, document: doc } = payload;
  const ui = subjectUi(task.subject);
  const { toast } = useToast();
  const [responses, setResponses] = useState<Record<string, unknown>>(attempt.responses || {});
  const [notes, setNotes] = useState<Record<string, string>>(attempt.notes || {});
  const [part, setPart] = useState(Math.min(attempt.currentPart || 0, doc.parts.length - 1));
  const [saveState, setSaveState] = useState<"saved" | "saving" | "dirty" | "error">("saved");
  const [showSources, setShowSources] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [elapsed, setElapsed] = useState(attempt.timeSpentSeconds || 0);
  const unsaved = useRef(0);
  const dirty = useRef(false);
  const latest = useRef({ responses, notes, part });
  latest.current = { responses, notes, part };

  // Clock: counts only while the tab is visible; seconds are flushed with each save.
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === "visible") { unsaved.current += 1; setElapsed(e => e + 1); } }, 1000);
    return () => clearInterval(t);
  }, []);

  const flush = useCallback(async (opts: { keepalive?: boolean } = {}) => {
    const body = { responses: latest.current.responses, notes: latest.current.notes, currentPart: latest.current.part, timeSpent: unsaved.current };
    const seconds = unsaved.current;
    unsaved.current = 0;
    dirty.current = false;
    setSaveState("saving");
    try {
      const r = await fetch(`/api/performance-tasks/attempts/${attempt.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body), keepalive: opts.keepalive });
      if (!r.ok) throw new Error(String(r.status));
      setSaveState("saved");
    } catch {
      unsaved.current += seconds;
      dirty.current = true;
      setSaveState("error");
    }
  }, [attempt.id]);

  // Debounced autosave
  useEffect(() => {
    dirty.current = true;
    setSaveState(s => (s === "saving" ? s : "dirty"));
    const t = setTimeout(() => { if (dirty.current) flush(); }, 1500);
    return () => clearTimeout(t);
  }, [responses, notes, part, flush]);
  // Periodic save keeps the clock honest even when nothing changes.
  useEffect(() => { const t = setInterval(() => { if (unsaved.current >= 30) flush(); }, 30_000); return () => clearInterval(t); }, [flush]);
  useEffect(() => {
    const onHide = () => { if (dirty.current || unsaved.current > 0) flush({ keepalive: true }); };
    const onVisibility = () => { if (document.visibilityState === "hidden") onHide(); };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => { window.removeEventListener("pagehide", onHide); document.removeEventListener("visibilitychange", onVisibility); };
  }, [flush]);

  const submit = useMutation({
    mutationFn: async () => {
      const body = { responses: latest.current.responses, notes: latest.current.notes, currentPart: latest.current.part, timeSpent: unsaved.current };
      unsaved.current = 0;
      return (await apiRequest("POST", `/api/performance-tasks/attempts/${attempt.id}/submit`, body)).json() as Promise<{ status: string; xpAwarded: number; levelUp: boolean; pendingMarker: number }>;
    },
    onSuccess: (r) => {
      if (r.xpAwarded > 0) toast({ title: `+${r.xpAwarded} XP`, description: r.levelUp ? "Level up!" : "Task marked." });
      else if (r.pendingMarker > 0) toast({ title: "Submitted", description: "Owlbert is marking your written answers now." });
      onSubmitted();
    },
    onError: () => toast({ title: "Could not submit", description: "Check your connection and try again.", variant: "destructive" }),
  });

  const items = useMemo(() => doc.parts.flatMap(p => p.items), [doc]);
  const unanswered = items.filter(i => !isAnswered(i, responses[i.id]));
  const current = doc.parts[part];
  const answeredInPart = (p: typeof current) => p.items.filter(i => isAnswered(i, responses[i.id])).length;
  const overTime = elapsed > task.timeMinutes * 60;
  const setResponse = (id: string, v: unknown) => setResponses(r => ({ ...r, [id]: v }));
  const go = (i: number) => { setPart(i); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const sourcesPanel = doc.sources.length > 0 && (
    <SourceViewer sources={doc.sources} notes={notes} notesEnabled={doc.notesEnabled} onNote={(id, v) => setNotes(n => ({ ...n, [id]: v }))} />
  );

  return (
    <div className="min-h-screen bg-background">
      <Confetti active={false} />
      <div className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border">
        <div className="container max-w-6xl mx-auto px-3 md:px-6 py-2 flex items-center gap-2">
          <Button variant="ghost" size="sm" className="p-2" onClick={() => { flush(); onExit(); }} aria-label="Save and exit" data-testid="button-exit-task"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0 flex-1">
            <p className="font-bold truncate text-sm md:text-base">{task.title}</p>
            <p className="text-[11px] text-muted-foreground truncate">{task.subject} · Grade {task.grade} · Attempt {attempt.attemptNumber}</p>
          </div>
          <span className={`hidden sm:inline-flex items-center gap-1 text-xs font-medium ${saveState === "error" ? "text-rose-600" : "text-muted-foreground"}`} data-testid="save-state">
            {saveState === "saving" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saveState === "error" ? <Save className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
            {saveState === "saving" ? "Saving" : saveState === "error" ? "Not saved, retrying" : saveState === "dirty" ? "Unsaved" : "Saved"}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${overTime ? "bg-amber-500/15 text-amber-800 dark:text-amber-200" : "bg-muted"}`} title={`Suggested time ${task.timeMinutes} minutes`} data-testid="task-clock">
            <Clock className="w-3.5 h-3.5" /> {formatDuration(elapsed)}
          </span>
        </div>
        <div className="container max-w-6xl mx-auto px-3 md:px-6 pb-2 flex gap-1.5 overflow-x-auto">
          {doc.parts.map((p, i) => {
            const done = answeredInPart(p) === p.items.length;
            return (
              <button key={p.id} type="button" onClick={() => go(i)}
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold border-2 inline-flex items-center gap-1 ${i === part ? `${ui.sticker} border-transparent` : done ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-800 dark:text-emerald-200" : "bg-card border-border"}`} data-testid={`part-tab-${i}`}>
                {done && i !== part && <Check className="w-3 h-3" />} Part {i + 1}
              </button>
            );
          })}
        </div>
      </div>

      <div className="container max-w-6xl mx-auto p-3 md:p-6">
        <div className={`grid gap-5 ${doc.sources.length ? "lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]" : ""}`}>
          {doc.sources.length > 0 && (
            <aside>
              <div className="lg:hidden mb-3">
                <button type="button" onClick={() => setShowSources(s => !s)} className="w-full rounded-xl border border-border bg-card px-4 py-3 flex items-center justify-between font-semibold text-sm" data-testid="toggle-sources">
                  <span className="inline-flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" /> Sources ({doc.sources.length})</span>
                  {showSources ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showSources && <div className="mt-3 rounded-xl border border-border bg-card p-3 max-h-[70vh] overflow-y-auto">{sourcesPanel}</div>}
              </div>
              <div className="hidden lg:block sticky top-[104px] rounded-xl border border-border bg-card p-4 max-h-[calc(100vh-120px)] overflow-y-auto">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Sources</p>
                {sourcesPanel}
              </div>
            </aside>
          )}

          <main className="space-y-4 min-w-0">
            <div className={`rounded-xl border ${ui.card} p-4`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Part {part + 1} of {doc.parts.length}</p>
              <h2 className="text-xl font-bold">{current.title}</h2>
              {current.intro && <TaskText text={current.intro} className="text-sm mt-2" />}
            </div>

            {current.items.map(item => (
              <ItemCard key={item.id} item={item} value={responses[item.id]} onChange={v => setResponse(item.id, v)} />
            ))}

            <div className="flex items-center justify-between gap-3 pt-2">
              <Button variant="outline" className="rounded-xl" disabled={part === 0} onClick={() => go(part - 1)} data-testid="button-prev-part"><ArrowLeft className="w-4 h-4 mr-1" /> Previous</Button>
              <span className="text-xs text-muted-foreground tabular-nums">{items.length - unanswered.length}/{items.length} answered</span>
              {part < doc.parts.length - 1 ? (
                <Button className={`rounded-xl font-bold ${ui.button}`} onClick={() => go(part + 1)} data-testid="button-next-part">Next part <ArrowRight className="w-4 h-4 ml-1" /></Button>
              ) : (
                <Button className="rounded-xl font-bold" onClick={() => setConfirm(true)} disabled={submit.isPending} data-testid="button-submit-task">{submit.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Submit for marking</Button>
              )}
            </div>
          </main>
        </div>
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{unanswered.length ? `${unanswered.length} question${unanswered.length === 1 ? "" : "s"} still blank` : "Submit your task?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {unanswered.length
                ? `Blank answers score zero. Unanswered: ${unanswered.map(i => i.label || i.id).join(", ")}. You can go back and finish them, or submit now.`
                : "Once you submit you cannot change your answers. Owlbert marks the written parts and you get feedback on every question."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-submit-cancel">Go back</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirm(false); submit.mutate(); }} data-testid="button-submit-confirm">{unanswered.length ? "Submit anyway" : "Submit"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ItemCard({ item, value, onChange, review }: { item: TaskItem; value: unknown; onChange?: (v: unknown) => void; review?: { score?: ItemScore; showKey: boolean; responses: Record<string, unknown> } }) {
  const answered = isAnswered(item, value);
  return (
    <Card className="rounded-xl" data-testid={`item-${item.id}`}>
      <CardContent className="p-4 md:p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-lg px-2.5 py-1 text-xs font-bold ${answered || review ? "bg-primary text-primary-foreground" : "bg-muted"}`}>Question {item.label || item.id}</span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{ITEM_TYPE_LABEL[item.type]}</span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground tabular-nums ml-auto">{item.points} mark{item.points === 1 ? "" : "s"}</span>
        </div>
        <TaskText text={item.prompt} className="text-sm font-medium" />
        {item.hint && !review && <details className="text-xs text-muted-foreground"><summary className="cursor-pointer inline-flex items-center gap-1"><Lightbulb className="w-3.5 h-3.5" /> Hint</summary><p className="mt-1">{item.hint}</p></details>}
        <ItemInput item={item} value={value} onChange={onChange} disabled={!onChange} review={review} />
      </CardContent>
    </Card>
  );
}

// ---------- Results ----------

function Results({ payload, onAgain, starting, onBack }: { payload: AttemptPayload; onAgain: () => void; starting: boolean; onBack: () => void }) {
  const { attempt, task, document: doc } = payload;
  const ui = subjectUi(task.subject);
  const pending = attempt.status === "submitted";
  const needsReview = attempt.status === "needs_review";
  const level = attempt.level;
  const pct = attempt.percent ?? 0;
  const scores = attempt.itemScores || {};
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => { if (attempt.status === "graded" && (level === "Proficient" || level === "Highly Proficient")) { setCelebrate(true); const t = setTimeout(() => setCelebrate(false), 3500); return () => clearTimeout(t); } }, [attempt.status, level]);
  const [showSources, setShowSources] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Confetti active={celebrate} />
      <div className="bg-primary text-primary-foreground p-4 md:p-6">
        <div className="container max-w-4xl mx-auto">
          <Button variant="ghost" className="text-white hover:bg-white/20 p-2 mb-2" onClick={onBack} data-testid="button-back-tasks"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold">{task.subject}</span>
            <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold">Grade {task.grade}</span>
            <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold">Attempt {attempt.attemptNumber}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{task.title}</h1>
        </div>
      </div>

      <div className="container max-w-4xl mx-auto p-4 md:p-6 space-y-5">
        <Card className="rounded-2xl overflow-hidden"><CardContent className="p-0">
          <div className="grid md:grid-cols-[auto_1fr] gap-4 p-4 md:p-6 items-center">
            <Illustration name={attempt.status === "graded" ? (pct >= 65 ? "owl-cheer" : "owl-chart") : "owl-book"} size={120} className="mx-auto" />
            <div className="space-y-2">
              {pending ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Marking in progress</p>
                  <p className="text-2xl font-bold flex items-center gap-2"><Loader2 className="w-6 h-6 animate-spin text-primary" /> Owlbert is marking your written answers</p>
                  <p className="text-sm text-muted-foreground">The multiple choice and calculation parts are marked already. Written answers usually take under a minute. This page updates itself.</p>
                  {attempt.gradingError && attempt.gradingAttempts > 1 && <p className="text-xs text-amber-700 dark:text-amber-300">Marking is taking longer than usual. We keep trying. You will get a notification when it is done.</p>}
                </>
              ) : needsReview ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Marking delayed</p>
                  <p className="text-2xl font-bold flex items-center gap-2"><Hourglass className="w-6 h-6 text-primary" /> Owlbert needs a little longer with your written answers</p>
                  <p className="text-sm text-muted-foreground">Your multiple choice and calculation marks are in. The written parts are queued and will be marked as soon as possible. You will get a notification, so you can carry on with something else.</p>
                </>
              ) : (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your result</p>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <p className="text-4xl font-bold tabular-nums">{attempt.score} <span className="text-xl text-muted-foreground font-semibold">/ {attempt.maxScore}</span></p>
                    <p className="text-lg font-semibold tabular-nums">{Math.round(pct)}%</p>
                    {level && <span className={`rounded-full px-3 py-1 text-sm font-bold ${LEVEL_UI[level].chip}`}>{level}</span>}
                  </div>
                  {level && <p className="text-sm text-muted-foreground">{LEVEL_UI[level].blurb}</p>}
                </>
              )}
              <div className="rounded-full h-2.5 bg-muted overflow-hidden"><div className={`h-full ${level ? LEVEL_UI[level].bar : ui.bar}`} style={{ width: `${Math.max(2, pct)}%` }} /></div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {formatDuration(attempt.timeSpentSeconds)} spent</span>
                {attempt.xpAwarded > 0 && <span className="inline-flex items-center gap-1 font-semibold text-primary"><Sparkles className="w-3.5 h-3.5" /> +{attempt.xpAwarded} XP</span>}
                {attempt.status === "graded" && attempt.xpAwarded === 0 && attempt.attemptNumber > 1 && <span>XP is only added when you beat your previous best.</span>}
              </div>
            </div>
          </div>
          <div className="border-t border-border bg-muted/30 px-4 md:px-6 py-3 flex flex-wrap gap-2">
            <Button className={`rounded-lg font-semibold ${ui.button}`} onClick={onAgain} disabled={starting} data-testid="button-try-again">{starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />} Try again</Button>
            <Button variant="outline" className="rounded-lg" onClick={onBack} data-testid="button-all-tasks">All tasks</Button>
            {doc.sources.length > 0 && <Button variant="ghost" className="rounded-lg ml-auto" onClick={() => setShowSources(s => !s)} data-testid="button-toggle-sources"><BookOpen className="w-4 h-4 mr-2" /> {showSources ? "Hide sources" : "Show sources"}</Button>}
          </div>
        </CardContent></Card>

        {showSources && <Card className="rounded-xl"><CardContent className="p-4 md:p-5"><SourceViewer sources={doc.sources} notes={attempt.notes} notesEnabled={false} /></CardContent></Card>}

        {attempt.aiFeedback && (attempt.aiFeedback.summary || attempt.aiFeedback.strengths?.length) && (
          <Card className="rounded-xl border-primary/30"><CardContent className="p-4 md:p-5 space-y-3">
            <h2 className="font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Owlbert's feedback</h2>
            {attempt.aiFeedback.summary && <p className="text-sm">{attempt.aiFeedback.summary}</p>}
            <div className="grid sm:grid-cols-2 gap-3">
              {attempt.aiFeedback.strengths?.length > 0 && (
                <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/25 p-3">
                  <p className="text-sm font-semibold flex items-center gap-1 text-emerald-800 dark:text-emerald-200"><Trophy className="w-4 h-4" /> What you did well</p>
                  <ul className="list-disc pl-5 text-sm mt-1 space-y-0.5">{attempt.aiFeedback.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
              )}
              {attempt.aiFeedback.improvements?.length > 0 && (
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/25 p-3">
                  <p className="text-sm font-semibold flex items-center gap-1 text-amber-800 dark:text-amber-200"><Target className="w-4 h-4" /> To improve</p>
                  <ul className="list-disc pl-5 text-sm mt-1 space-y-0.5">{attempt.aiFeedback.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
              )}
            </div>
            {attempt.aiFeedback.nextSteps && attempt.aiFeedback.nextSteps.length > 0 && (
              <div className="rounded-xl bg-primary/5 border border-primary/20 p-3">
                <p className="text-sm font-semibold flex items-center gap-1"><Star className="w-4 h-4 text-primary" /> Next steps</p>
                <ul className="list-disc pl-5 text-sm mt-1 space-y-0.5">{attempt.aiFeedback.nextSteps.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
            )}
          </CardContent></Card>
        )}

        {doc.parts.map((p, pi) => {
          const partScore = p.items.reduce((s, i) => s + (scores[i.id]?.score ?? 0), 0);
          const partMax = p.items.reduce((s, i) => s + i.points, 0);
          const partPending = p.items.some(i => !scores[i.id] || scores[i.id].status === "pending");
          return (
            <section key={p.id} className="space-y-3">
              <div className={`rounded-xl border ${ui.card} p-4 flex items-center justify-between gap-3`}>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Part {pi + 1}</p>
                  <h2 className="text-lg font-bold">{p.title}</h2>
                </div>
                <span className="rounded-full bg-card border border-border px-3 py-1 text-sm font-bold tabular-nums">{partPending ? `${Math.round(partScore * 10) / 10}+` : Math.round(partScore * 10) / 10} / {partMax}</span>
              </div>
              {p.items.map(item => (
                <ItemCard key={item.id} item={item} value={attempt.responses[item.id]} review={{ score: scores[item.id], showKey: true, responses: attempt.responses }} />
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
